<template>
  <VdCard
    ref="cardRef"
    class="configurations-list-item-component"
    size="small"
    expandable
    :expanded="isCardExpanded"
    @update:expanded="onCardExpandedChanged"
  >
    <div class="configurations-list-item-content">
      <div class="name">
        <h3 class="displayed">
          {{ configurationClass.displayName }}
        </h3>
        <h5 class="technical">{{ configurationClass.technicalName }}</h5>
      </div>

      <div class="control">
        <VdSwitch
          v-if="isCardExpanded && configurationClass.isMultiInstance"
          v-model="allExpanded"
          active-text="Expand all rules"
          @update:model-value="onExpanded"
        />
        <vd-dropdown v-if="inWipVersion && canWriteClasses && isClassDefault">
          <vd-dropdown-item @click="onEditClass">Edit class</vd-dropdown-item>
        </vd-dropdown>
        <VdTooltip v-if="!isClassDefault" :content="`Cannot edit a class of type ${configurationClass.type}`">
          <VdIcon store="fa" color="var(--vd-base-primary-color)" name="circle-info" size="large" />
        </VdTooltip>
      </div>
    </div>
    <template #expanded-content>
      <p v-if="configurationClass.description" class="description">{{ configurationClass.description }}</p>
    </template>
    <template #expanded-footer>
      <div v-if="!shouldRenderTable" class="table-placeholder" :style="{ height: estimatedTableHeight }"></div>
      <DynamicTable
        v-else
        :key="tableData.map((o) => o.id).join('')"
        handle=".handle"
        fixed-table
        :sub-levels="configurationClass.isMultiInstance"
        :no-drag-drop="!inWipVersion"
        @drop="onRuleDropped"
      >
        <el-table
          ref="tableRef"
          :data="tableData"
          style="width: 100%"
          :row-class-name="
            ({ rowIndex }) => (rowIndex === tableData.length - 1 ? 'disabled' : `configuration-row-${rowIndex}`)
          "
          row-key="id"
          :tree-props="{ children: 'instances' }"
        >
          <el-table-column prop="name" fixed width="250px" class-name="rule-name">
            <template #header>
              <div class="priority-header">
                <div>Priority</div>
                <VdTooltip
                  class="item"
                  orientation="top"
                  content="You can drag and drop the segment lines to manage the priorities thanks to the handles: ≡≡"
                >
                  <vd-custom-icon name="info-icon" big class="info-icon" />
                </VdTooltip>
              </div>
            </template>
            <template #default="scope">
              <div
                :class="{
                  handle: scope.row.showHandle,
                  'default-segment': scope.row.isDefault && !scope.row.isInstance,
                }"
              >
                <vd-custom-icon v-if="scope.row.showHandle" name="handle-icon" width="20" />
                <vd-custom-icon
                  v-else-if="scope.row.isDefault && !scope.row.isInstance"
                  name="lock-icon"
                  width="14"
                  box="0 0 10 14"
                  color="var(--vd-color-grey)"
                />
                <div v-else></div>
                <div>
                  <component
                    :is="scope.row.displayAsLink && canWriteConfigurations ? 'router-link' : 'div'"
                    :to="getRuleEditRoute(scope.row)"
                    :class="{ 'is-default-value': scope.row.isDefault }"
                  >
                    {{ scope.row.name }}

                    <VdTooltip
                      v-if="scope.row.isDefault"
                      content="Edit the class to modify those values."
                      orientation="top"
                    >
                      <VdIcon class="is-default-icon-info" store="fa" name="circle-info" size="small" />
                    </VdTooltip>
                  </component>
                  <PlatformGameVersionsLabel v-if="scope.row.platform" :value="scope.row.platform" />
                  <ScheduleLabel v-if="scope.row.schedule" :schedule="scope.row.schedule" />
                  <ConfigurationSizeChangeDisplay
                    v-if="!scope.row.isDefault && scope.row.gapInSize !== null && scope.row.gapInSize !== undefined"
                    :gap-in-size="scope.row.gapInSize"
                  ></ConfigurationSizeChangeDisplay>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            v-for="attribute in configurationClass.attributes"
            :key="attribute.technicalName"
            :label="attribute.displayName"
            :min-width="getWidthOfAttributeColumn(attribute, tableData)"
            :class-name="getClassForAttributeColumn(attribute)"
            show-overflow-tooltip
            align="left"
          >
            <template #default="scope">
              <div v-if="scope.row.isClassicRow">
                <div
                  :class="{
                    'is-default-value': scope.row.isDefault,
                    overridden: scope.row.attributes[attribute.technicalName].isOverridden,
                  }"
                >
                  <div v-if="attribute.isArrayType" class="attribute-tags-container">
                    <span class="attribute-tags">
                      <span
                        v-for="(item, index) in scope.row.values[attribute.technicalName] || []"
                        :key="`${attribute.technicalName}-tags-${index}`"
                      >
                        {{ item }}
                      </span>
                    </span>
                    <VdTooltip
                      :content="tooltipContent(scope, attribute)"
                      :disabled="!scope.row.attributes[attribute.technicalName].isOverridden"
                    >
                      <VdIcon
                        v-show="scope.row.attributes[attribute.technicalName].isOverridden"
                        store="fa"
                        color="var(--vd-base-primary-color)"
                        name="circle-info"
                        size="large"
                      />
                    </VdTooltip>
                  </div>
                  <div v-else-if="attribute.type === 'json'" class="attribute-tags-container">
                    <JsonEditor :model-value="scope.row.values[attribute.technicalName]" readonly />
                    <VdTooltip
                      :content="tooltipContent(scope, attribute)"
                      :disabled="!scope.row.attributes[attribute.technicalName].isOverridden"
                    >
                      <VdIcon
                        v-show="scope.row.attributes[attribute.technicalName].isOverridden"
                        store="fa"
                        color="var(--vd-base-primary-color)"
                        name="circle-info"
                        size="large"
                      />
                    </VdTooltip>
                  </div>

                  <div v-else>
                    <span v-if="isClassDefault" class="attribute-value">
                      {{ scope.row.values[attribute.technicalName] }}
                    </span>
                    <span v-if="!isClassDefault" class="attribute-value">
                      {{ getShopName(scope.row.values[attribute.technicalName]) }}
                    </span>
                    <VdTooltip
                      :content="tooltipContent(scope, attribute)"
                      :disabled="
                        scope.row.isDefault ||
                        (!scope.row.attributes[attribute.technicalName].isOverridden &&
                          !configurationClass.dynamicInstances)
                      "
                    >
                      <VdIcon
                        v-show="
                          !scope.row.isDefault &&
                          (scope.row.attributes[attribute.technicalName].isOverridden ||
                            configurationClass.dynamicInstances)
                        "
                        store="fa"
                        color="var(--vd-base-primary-color)"
                        name="circle-info"
                        size="large"
                      />
                    </VdTooltip>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <!-- menu actions column -->
          <el-table-column v-if="inWipVersion" fixed="right" label="" width="60">
            <template #default="scope">
              <VdTooltip
                v-if="scope.row.isDefault && !isClassDefault"
                :content="`Cannot edit default value on class of type ${configurationClass.type}`"
              >
                <VdIcon store="fa" color="var(--vd-base-primary-color)" name="circle-info" size="large" />
              </VdTooltip>

              <vd-dropdown v-if="canEditRow(scope.row)">
                <vd-dropdown-item
                  v-if="
                    scope.row.isDefault
                      ? canWriteClasses && configurationClass.type === 'DEFAULT'
                      : canWriteConfigurations
                  "
                  @click="onConfigurationRuleEdit(scope.row)"
                >
                  {{ scope.row.isDefault ? "Edit class values" : "Edit rule" }}
                </vd-dropdown-item>
                <vd-dropdown-item
                  v-if="!scope.row.isDefault && canWriteConfigurations"
                  @click="onConfigurationRuleDuplicate(scope.row.id)"
                >
                  Duplicate
                </vd-dropdown-item>
                <vd-dropdown-item
                  v-if="!scope.row.isDefault && canWriteConfigurations"
                  dangerous
                  @click="onConfigurationRuleDelete(scope.row.id)"
                >
                  Delete
                </vd-dropdown-item>
              </vd-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </DynamicTable>

      <div class="add-rule-button-wrapper">
        <vd-button
          v-if="inWipVersion && canWriteConfigurations"
          type="secondary"
          skin="blue"
          icon-left="fa:add"
          class="add-rule-button"
          @click="onNewConfigurationRule"
        >
          Add rule
        </vd-button>
      </div>
    </template>
  </VdCard>
</template>

<script setup lang="ts">
import { VdCard, VdIcon, VdSwitch, VdTooltip } from "@voodoo.io/voodoo-ui";
import { useIntersectionObserver } from "@vueuse/core";
import { ElTable } from "element-plus";
import { PropType, computed, defineAsyncComponent, defineComponent, h, nextTick, ref, watch } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";

import JsonEditor from "@/components/JsonEditor/JsonEditor.vue";
import {
  AttributeValue,
  ClassAttributeInfoFragment,
  ClassAttributeValueFragment,
  ClassInstanceFragment,
  ConfigurationClassAttributeValueFragment,
  ConfigurationClassFragment,
  ConfigurationRuleFragment,
  ConfigurationRuleInstanceFragment,
  ConfigurationRuleSchedule,
  GamePlatformFragment,
  useConfigurationsListItemSortMutation,
  useMutationResponse,
} from "@/graphql";
import usePermissions from "@/hooks/use-permissions";
import { Shop } from "@/modules/Store/shop.types";
import PlatformGameVersionsLabel from "@/pages/games/GameDisplay/children/configurations/components/PlatformGameVersionsLabel.vue";
import ScheduleLabel from "@/pages/games/GameDisplay/children/configurations/components/ScheduleLabel.vue";
import links from "@/router/links";

type TableRow = {
  id: string | null; // ruleId or ruleId + instanceId
  name: string; // segmentName (parent) or instance name
  showHandle: boolean;
  displayAsLink: boolean;
  isClassicRow: boolean;
  isInstance: boolean;
  isDefault: boolean;
  platform?: GamePlatformFragment;
  schedule?: ConfigurationRuleSchedule | null;
  attributes: Record<string, ClassAttributeValueFragment | ConfigurationClassAttributeValueFragment> | null;
  values: Record<string, AttributeValue> | null;
  instances?: TableRow[];
  gapInSize: number | null;
};

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },

  versionId: {
    type: String,
    required: true,
  },

  /**
   * The actual class object
   */
  configurationClass: {
    type: Object as PropType<ConfigurationClassFragment>,
    required: true,
  },

  /**
   * Wether we are in a WIP version or not
   */
  inWipVersion: {
    type: Boolean,
    required: true,
  },

  /**
   * If true, the card is expanded
   */
  expanded: {
    type: Boolean,
    default: false,
  },

  /**
   * Shops of the version, resolved once by the page rather than per card
   */
  shops: {
    type: Array as PropType<Shop[]>,
    default: () => [],
  },
});

const emit = defineEmits(["delete"]);

const router = useRouter();
const { canWriteConfigurations, canWriteClasses } = usePermissions();

const tableRef = ref<InstanceType<typeof ElTable>>();
const rulesOrder = ref<string[]>([]);

const cardRef = ref();
/** Latches, so a table that has been built is never torn down and rebuilt while scrolling. */
const hasBeenVisible = ref(false);

useIntersectionObserver(
  cardRef,
  ([entry]) => {
    if (entry?.isIntersecting) {
      hasBeenVisible.value = true;
    }
  },
  { rootMargin: "600px" }
);

const TABLE_ROW_HEIGHT = 48;
const TABLE_HEADER_HEIGHT = 40;

/** Instance sub-rows start collapsed, so the visible rows are the rules plus the default row. */
const estimatedTableHeight = computed(
  () => `${(props.configurationClass.rules.length + 1) * TABLE_ROW_HEIGHT + TABLE_HEADER_HEIGHT}px`
);

/**
 * Handle card toggle feature
 */
const isCardExpanded = ref(props.expanded);

/** The chevron toggles VdCard's own state and only announces it through this event. */
const onCardExpandedChanged = (expanded: boolean) => {
  isCardExpanded.value = expanded;
};

/** Gates the `expanded-footer` slot, which VdCard only renders while open, so expansion is implied. */
const shouldRenderTable = computed(() => hasBeenVisible.value);

watch(
  () => props.expanded,
  (watchedExpanded) => {
    isCardExpanded.value = watchedExpanded;
  }
);

/**
 * Handle toggle table rows & instances filter
 */
const allExpanded = ref(false);
const visibleInstances = ref(props.configurationClass.instances.map((inst) => inst.id));

const isClassDefault = computed(() => props.configurationClass.type === "DEFAULT");

const tooltipContent = (
  scope: { row: { attributes: Record<string, { defaultValue: string }> } },
  attribute: { technicalName: string }
) => {
  const value = scope.row.attributes[attribute.technicalName].defaultValue;

  return `This rule overrides the default configuration value${value ? " :" : "."}${
    scope.row.attributes[attribute.technicalName].defaultValue
  }`;
};

const filteredInstances = computed(() =>
  props.configurationClass.instances.filter((inst) => visibleInstances.value.includes(inst.id))
);

const configurationRules = computed(() =>
  props.configurationClass.rules.map((rule) => ({
    ...rule,
    instances: rule.instances,
  }))
);

const sizeGapPerRule = computed(() => {
  const response = new Map();
  props.configurationClass.rules.forEach((rule) => {
    const gapInSize = rule.instances.reduce((acc, currentValue) => acc + currentValue.gapInSize ?? 0, 0);
    const ruleId = rule.id;
    response.set(ruleId, gapInSize);
  });
  return response;
});

const VDDraggableTable = defineAsyncComponent(
  () => import("@/components/structure/VDDraggableTable/VDDraggableTable.vue")
);
const DynamicTable = defineComponent({
  setup(_, { slots }) {
    const component = canWriteConfigurations ? VDDraggableTable : "div";

    return () => h(component, {}, slots);
  },
});

/**
 * Build all the types of table rows (parent, instances, default and classic rows)
 */
const buildTableRow = ({
  instance,
  rule,
  isMultiInstance,
}: {
  instance?: ClassInstanceFragment | ConfigurationRuleInstanceFragment;
  rule?: ConfigurationRuleFragment;
  isMultiInstance: boolean;
}): TableRow => {
  /**
   * If no rule is defined, we assume that we are on default values
   */

  /**
   * Defining the instance used in the function
   */
  let currentInstance = instance;

  /**
   * If we are on one instance, we use it for the rest of the function (that is the stop condition)
   */
  if (!isMultiInstance && !instance) {
    const [inst] = rule?.instances ?? filteredInstances.value;
    currentInstance = inst;
  }

  const buildId = () => {
    if (!rule) {
      return isMultiInstance && currentInstance ? currentInstance.id : "default";
    }
    if (isMultiInstance && currentInstance) {
      return `${rule.id}-${currentInstance.id}`;
    }
    return rule.id;
  };

  const buildName = () => {
    if (!rule) {
      return isMultiInstance && currentInstance ? currentInstance.name : "Default values";
    }
    if (isMultiInstance && currentInstance) {
      return currentInstance.name;
    }
    return rule.segment.name;
  };

  const buildPlatform = () => {
    if (!rule || (isMultiInstance && currentInstance)) {
      return undefined;
    }

    return rule.platform;
  };

  const buildSchedule = () => {
    if (!rule) {
      return undefined;
    }

    return rule.schedule;
  };

  /**
   * Calling recursively the build row function in order to create the line for each instance
   */
  const buildInstances = () => {
    if (isMultiInstance && !currentInstance) {
      const instances: (ClassInstanceFragment | ConfigurationRuleInstanceFragment)[] = !rule
        ? filteredInstances.value
        : rule.instances;

      return instances.map((inst, index) =>
        buildTableRow({
          instance: { ...inst, name: `Instance ${index + 1}` },
          rule,
          isMultiInstance,
        })
      );
    }
    return undefined;
  };

  let values: Record<string, AttributeValue> | null = null;
  let attributes: Record<string, ClassAttributeValueFragment | ConfigurationClassAttributeValueFragment> | null = null;

  /**
   * Building the values for one row
   */
  if (currentInstance) {
    const instanceValue: (ClassAttributeValueFragment | ConfigurationClassAttributeValueFragment)[] =
      currentInstance.values;
    values = Object.fromEntries(
      instanceValue.map((attrValue) => {
        const value = "value" in attrValue ? attrValue.value : attrValue.defaultValue;
        return [attrValue.technicalName, value];
      })
    );

    attributes = Object.fromEntries(instanceValue.map((attrValue) => [attrValue.technicalName, attrValue]));
  }

  return {
    id: buildId(),
    name: buildName(),
    isClassicRow: !isMultiInstance || (!!currentInstance && isMultiInstance),
    isInstance: !!currentInstance && isMultiInstance,
    showHandle:
      ((!currentInstance && !!rule) || (!isMultiInstance && !!rule)) &&
      props.inWipVersion &&
      canWriteConfigurations.value,
    displayAsLink: (!currentInstance && !!rule) || (!isMultiInstance && !!rule),
    platform: buildPlatform(),
    schedule: buildSchedule(),
    isDefault: !rule,
    attributes,
    values,
    instances: buildInstances(),
    gapInSize:
      (!currentInstance && !!rule) || (!isMultiInstance && !!rule) ? sizeGapPerRule.value.get(rule?.id) ?? 0 : null,
  };
};

const tableData = computed<TableRow[]>(() => {
  const rulesById = Object.fromEntries(configurationRules.value.map((rule) => [rule.id, rule]));

  let rules = configurationRules.value;
  if (rulesOrder.value.length) {
    rules = rulesOrder.value.map((ruleId) => rulesById[ruleId]);
  }

  const defaultRuleRow = buildTableRow({ isMultiInstance: props.configurationClass.isMultiInstance });
  const rows: TableRow[] = rules.map((rule) =>
    buildTableRow({ rule, isMultiInstance: props.configurationClass.isMultiInstance })
  );

  return [...rows, defaultRuleRow];
});
/**
 * Activate expansion for each row of the table
 */
const onExpanded = async (isExpanded: boolean) => {
  if (isExpanded && !visibleInstances.value.length) {
    visibleInstances.value = props.configurationClass.instances.map((inst) => inst.id);
  }
  await nextTick();
  tableData.value.forEach((row) => tableRef?.value?.toggleRowExpansion(row, isExpanded));
};

/**
 * Let's make the width of each column corresponding to the possible
 * content that it may contain ↔️
 */
const getWidthOfAttributeColumn = (attribute: ClassAttributeInfoFragment, configData) => {
  const attributeValue = String(configData[1]?.attributes?.[attribute.technicalName]?.defaultValue);

  const maxLengthValueOrName = Math.max(attributeValue.length, attribute.displayName.length);

  const maxLengthLimited = maxLengthValueOrName > 25 ? 25 : maxLengthValueOrName;

  return `${maxLengthLimited * 10 + 25}px`;
};

const getClassForAttributeColumn = (attribute: ClassAttributeInfoFragment) => {
  if (attribute.type === "string" || attribute.isArrayType) {
    return "large-cell";
  }
  return "small-cell";
};

/**
 * Routing links
 */
const locationParams = {
  gameId: props.gameId,
  versionId: props.versionId,
  classId: props.configurationClass.technicalName,
};

const editClassLink: RouteLocationRaw = {
  name: links.games.classes.edit,
  params: locationParams,
};

const onEditClass = () => {
  router.push(editClassLink);
};

const previewClassLink: RouteLocationRaw = {
  name: links.games.classes.preview,
  params: locationParams,
};

const onNewConfigurationRule = () => {
  router.push({
    name: links.games.configurations.newRule,
    params: locationParams,
  });
};

const getRuleEditRoute = (row: TableRow): RouteLocationRaw | never => {
  if (row.isDefault) {
    return props.inWipVersion ? editClassLink : previewClassLink;
  }

  if (!row.id) {
    throw new Error("unknown rule id");
  }

  const name = props.inWipVersion ? links.games.configurations.editRule : links.games.configurations.previewRule;

  return {
    name,
    params: {
      ...locationParams,
      ruleId: row.id,
    },
  };
};

/**
 * Sorting
 */

const { mutate: sortRules } = useConfigurationsListItemSortMutation({});

const onRuleDropped = async ({ list }: { list: TableRow[] }) => {
  if (!props.inWipVersion || list == null) {
    return;
  }

  const ruleIds = list.filter((rule) => !rule.isDefault).map((rule) => rule.id as string);
  const response = await sortRules({
    gameId: props.gameId,
    versionId: props.versionId,
    classId: props.configurationClass.technicalName,
    ruleIds,
  });
  const data = useMutationResponse(response, (resp) => resp?.sortConfigurationRules);
  if (data?.success) {
    rulesOrder.value = ruleIds;
  }
};

/**
 * Menu actions
 */

const onConfigurationRuleEdit = (row: TableRow) => {
  router.push(getRuleEditRoute(row));
};

const onConfigurationRuleDuplicate = (ruleId: string) => {
  router.push({
    name: links.games.configurations.duplicateRule,
    params: {
      ...locationParams,
      ruleId,
    },
  });
};

const onConfigurationRuleDelete = (ruleId: string) => {
  emit("delete", ruleId);
};

/**
 * Shop section
 */
const getShopName = (shopId: string) => {
  const toBeDisplayedShop = props.shops.find((shop) => shop.id === shopId);
  return toBeDisplayedShop?.name ?? "";
};

const canEditRow = (row: TableRow): boolean => {
  if (row.isInstance && !row.isDefault) {
    return false;
  }

  if (row.isInstance && row.isDefault) {
    return canWriteClasses && props.configurationClass.type === "DEFAULT";
  }

  return !!canWriteConfigurations;
};
</script>

<style lang="scss">
.configurations-list-item-component .vd-card__expanded-section {
  padding: 1rem;
  padding-top: 0;

  .table-placeholder {
    margin-top: -1rem;
  }

  .el-table {
    color: var(--vd-color-text-default);
    border: 1px solid var(--vd-color-text-light-7);
    border-bottom: 1px;
    margin-top: -1rem;

    th.el-table__cell.is-leaf {
      background: var(--vd-color-text-light-9) !important;
    }

    .el-table__body td.el-table__cell:first-child {
      background: var(--vd-color-text-light-9) !important;
      border-right: 1px solid var(--vd-color-text-light-7);
    }
  }

  .el-table__row.disabled.el-table__row--level-0 td {
    background: var(--vd-color-text-light-9) !important;

    &:first-child {
      border-right: none;
    }
  }

  .el-table thead {
    color: var(--vd-color-text-default);
    font-weight: 700;

    .class-attribute-technical-name {
      font-size: font("S");
      color: var(--vd-color-text-light-2);
      margin-top: -4px;
      font-weight: 500;
    }
  }
}

.configurations-list-item-component {
  margin: 20px 0;

  .info-icon {
    margin-left: 10px;
  }

  .rule-name {
    .cell {
      display: flex;
      align-items: center;
    }
  }

  .el-table__placeholder {
    width: 10px;
  }

  .priority-header-tooltip {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    max-width: 270px;
    padding: 10px;
  }

  .priority-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-weight: 700;
    color: var(--vd-color-text-default);

    svg {
      fill: var(--vd-color-blue-default);
      width: 14px;
      min-width: 14px !important;
      margin-bottom: -6px;
    }
  }

  .handle,
  .default-segment {
    display: grid;
    align-items: center;
    justify-items: center;
    grid-template-columns: 20px 1fr;
    grid-column-gap: 10px;

    > .custom-icon-component {
      cursor: grab;
    }
  }

  .is-default-icon-info {
    color: $foggy-grey;
    margin-left: size("S");
  }

  .description {
    margin: 0;
    font-size: 14px;
    white-space: pre;
  }

  .attribute-value {
    @include ellipsis;
  }

  .attribute-tags {
    @extend %tags;
    margin-top: size("S");
    justify-content: flex-start;
  }

  .attribute-tags-container {
    display: flex;
    align-items: center;
  }

  .overridden:not(.is-default-value) .attribute-value {
    padding-right: 5px;
  }

  .overridden:not(.is-default-value) .attribute-tags span {
    padding-right: 5px;
  }

  .is-default-value {
    color: var(--vd-base-primary-text-color);
    position: relative;
  }

  .is-na {
    color: $red-alert;
    opacity: 0.7;
  }

  .el-tooltip__trigger svg {
    margin-left: 5px;
  }

  .configurations-list-item-content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .name {
      padding: size("M") 0;

      a {
        text-decoration: none;
      }

      .displayed {
        margin-bottom: size("M");
      }
      .technical {
        @extend %technical-name;
        color: var(--vd-base-regular-text-color);
      }
    }

    .control {
      display: flex;
      column-gap: size("ML");
      align-items: center;
    }
  }

  .add-rule-button-wrapper {
    background: var(--vd-color-text-light-9);
    border: 1px solid var(--vd-color-text-light-7);
    border-top: none;
    padding: 0.5rem;
  }
}
</style>
